import {
    CaseData,
    AssetModel,
    LiabilityModel,
    ExclusionItemModel,
    StepDownModel
} from '@/types/case';
import { isJudicialCase } from '@/utils/caseTeamType';

export function validateCaseInfo(caseData: CaseData): boolean {
    // Ensure metadata.errors exists and is initialized
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.caseInfo exists to preserve visited flag
    if (!caseData.validation.caseInfo) {
        caseData.validation.caseInfo = {};
    }

    // Ensure validation.childSupportDocs is reset to empty
    if (!caseData.validation.childSupportDocs) {
        caseData.validation.childSupportDocs = {};
    }
    caseData.validation.childSupportDocs!.errors = [];

    // Ensure validation.financialDocs is reset to empty
    if (!caseData.validation.financialDocs) {
        caseData.validation.financialDocs = {};
    }
    caseData.validation.financialDocs!.errors = [];


    // Clear errors before validation
    caseData.metadata.errors = [];

    var caseInfo = caseData.validation.caseInfo;
    caseInfo.errors = [];

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        caseInfo.errors.push('Missing parties');
    } else {

        if (!partyA.personal?.firstName) {
            caseInfo.errors.push('Missing party A first name');
        }
        if (!partyA.personal?.lastName) {
            caseInfo.errors.push('Missing party A last name');
        }
        if (!partyB.personal?.firstName) {
            caseInfo.errors.push('Missing party B first name');
        }
        if (!partyB.personal?.lastName) {
            caseInfo.errors.push('Missing party B last name');
        }
        if (caseData.representation?.petitionerParty !== 'partyA' &&
            caseData.representation?.petitionerParty !== 'partyB') {
            caseInfo.errors.push('Petitioner must be specified');
        }
        if (!isJudicialCase(caseData)) {
            if (caseData.representation?.representedParty !== 'partyA' &&
                caseData.representation?.representedParty !== 'partyB') {
                caseInfo.errors.push('My firm represents this party must be checked for at least one party');
            }
            if (!caseData.basic.attorneyListedOnDocuments?.id || !caseData.basic.attorneyListedOnDocuments?.firstName || !caseData.basic.attorneyListedOnDocuments?.lastName || !caseData.basic.attorneyListedOnDocuments?.teamName) {
                caseInfo.errors.push('Attorney must be specified');
            }
        }
        if (!caseData.basic.countyId) {
            caseInfo.errors.push('County must be specified');
        }
        if (!caseData.basic.taxYear) {
            caseInfo.errors.push('Tax year must be specified');
        }
    }

    // we get here, case info has been validated.
    const hasErrors = caseInfo.errors.length > 0;
    const isVisited = caseData.validation.caseInfo?.visited === true;
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(caseInfo.errors ?? []).map(val => 'Case Info: ' + val));
    caseData.validation.childSupportDocs!.errors!.push(...(caseInfo.errors ?? []).map(e => e));
    caseData.validation.financialDocs!.errors!.push(...(caseInfo.errors ?? []).map(e => e));

    if (hasErrors) {
        caseData.validation.caseInfo = {
            ...caseInfo,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.caseInfo = {
            ...caseInfo,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateCustody(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.custody exists to preserve visited flag
    if (!caseData.validation.custody) {
        caseData.validation.custody = {};
    }

    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var custody = caseData.validation.custody;
    custody.errors = [];

    const isVisited = caseData.validation.custody?.visited === true;

    // if minor children are not involved, no need to validate the rest of the custody page
    if (caseData.basic?.minorChildrenInvolved === false) {
        caseData.validation.custody = {
            ...caseData.validation.custody,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    // Track custody-specific errors (don't clear all errors, as caseInfo errors may still be present)
    // if children count is 0, no need to validate the rest of the custody page

    if ((caseData.children?.count ?? 0) <= 0) {
        custody.errors.push('At least one child must be specified');
    }
    if ((caseData.children?.custodyArrangement ?? '') === '') {
        custody.errors.push('Custody arrangement must be specified');
    }
    if ((caseData.children?.custodyArrangement ?? '') === 'partyA' || caseData.children?.custodyArrangement === 'partyB') {
        if ((caseData.children.overnightsPerYear ?? '') === '') {
            custody.errors.push('Court ordered overnights per year must be specified');
        }
    } else if (caseData.children?.custodyArrangement === 'split') {
        if ((caseData.children?.splits?.length ?? 0) < (caseData.children?.count ?? 0)) {
            custody.errors.push('At least one split must be specified for each child');
        } else {

            if ((caseData.children?.splits?.length ?? 0) > (caseData.children?.count ?? 0)) {
                // chop off splits that are no longer valid.
                caseData.children.splits = caseData.children.splits?.slice(0, caseData.children.count) ?? [];
            }

            for (let i = 0; i < (caseData.children?.splits?.length ?? 0); i++) {
                const arrangement = caseData.children?.splits?.[i]?.custodyArrangement ?? '';
                if (arrangement !== 'shared' && arrangement !== '') {
                    if ((caseData.children?.splits?.[i]?.overnightsPerYear ?? '') === '') {
                        custody.errors.push('Court ordered overnights per year must be specified for each child');
                    }
                } else if (arrangement === '') {
                    custody.errors.push('Custody arrangement must be specified for each child');
                }

            }
        }
    }

    // we get here, case info has been validated.
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(custody.errors ?? []).map(val => 'Custody: ' + val));

    caseData.validation.childSupportDocs!.errors!.push(...(custody.errors ?? []).map(e => e));

    if (custody.errors.length > 0) {
        caseData.validation.custody = {
            ...custody,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.custody = {
            ...custody,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}


export const ZERO_INCOME_ERROR = 'At least one income must be specified';

export function validateIncome(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.income exists to preserve visited flag
    if (!caseData.validation.income) {
        caseData.validation.income = {};
    }

    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var income = caseData.validation.income;
    income.errors = [];

    const isVisited = income.visited === true;

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        income.errors.push('Missing parties');
    } else {
        const partyATotal = (partyA.income?.primary?.yearlyWages ?? 0) + (partyA.income?.primary?.yearlySelfEmployment ?? 0) +
            (partyA.income?.others ?? []).reduce((sum, o) => sum + (o?.amount ?? 0), 0);
        const partyBTotal = (partyB.income?.primary?.yearlyWages ?? 0) + (partyB.income?.primary?.yearlySelfEmployment ?? 0) +
            (partyB.income?.others ?? []).reduce((sum, o) => sum + (o?.amount ?? 0), 0);
        if (partyATotal <= 0 && partyBTotal <= 0) {
            income.errors.push(ZERO_INCOME_ERROR);
        }
    }

    // we get here, income has been validated.
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(income.errors ?? []).map(val => 'Income: ' + val));
    // Zero income for both parties is a warning on the Income step only: it must not block any document.
    caseData.validation.childSupportDocs!.errors!.push(...(income.errors ?? []).filter(e => e !== ZERO_INCOME_ERROR));
    // Income errors should not block financialDocs: both parties may have $0 income

    if (income.errors.length > 0) {
        caseData.validation.income = {
            ...income,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.income = {
            ...income,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateTaxes(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.taxes exists to preserve visited flag
    if (!caseData.validation.taxes) {
        caseData.validation.taxes = {};
    }
    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var taxes = caseData.validation.taxes;
    taxes.errors = [];

    const isVisited = taxes.visited === true;

    // if minor children are not involved, no need to validate the rest of the taxes page
    if (caseData.basic?.minorChildrenInvolved === false) {
        caseData.validation.taxes = {
            ...caseData.validation.taxes,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    // Track taxes-specific errors (don't clear all errors, as caseInfo errors may still be present)
    // if children count is 0, no need to validate the rest of the taxes page

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        taxes.errors.push('Missing parties');
    } else {
        const schedule = caseData.children.taxSchedule ?? '';
        const childrenCount = caseData.children?.count ?? 0;

        // Helper function to validate combined dependent children totals for both parties and year type
        const validateDependentChildrenForYear = (yearType: 'all' | 'odd' | 'even') => {
            const partyAYearData = partyA?.tax?.claimSchedule?.[yearType];
            const partyBYearData = partyB?.tax?.claimSchedule?.[yearType];

            // Skip if year data doesn't exist for both parties
            if (!partyAYearData && !partyBYearData) {
                return;
            }

            // Get totals for each party
            const partyAAge5Under = partyAYearData?.age5Under ?? 0;
            const partyAAge6to16 = partyAYearData?.age6to16 ?? 0;
            const partyAAge17Over = partyAYearData?.age17Over ?? 0;
            const partyATotal = partyAAge5Under + partyAAge6to16 + partyAAge17Over;

            const partyBAge5Under = partyBYearData?.age5Under ?? 0;
            const partyBAge6to16 = partyBYearData?.age6to16 ?? 0;
            const partyBAge17Over = partyBYearData?.age17Over ?? 0;
            const partyBTotal = partyBAge5Under + partyBAge6to16 + partyBAge17Over;

            // Calculate combined total across both parties
            const combinedTotal = partyATotal + partyBTotal;

            if (combinedTotal > childrenCount) {
                const yearLabel = yearType === 'all' ? '' : yearType === 'odd' ? ' (Odd Years)' : ' (Even Years)';
                if (taxes.errors) {
                    taxes.errors.push(`The combined total number of dependent children (age 5 and under + ages 6-16 + age 17 and over) for both parties${yearLabel} (${combinedTotal}) exceeds the total number of children in the case (${childrenCount})`);
                }
            }
        };

        const isTaxStatusSpecified = (s: string | null | undefined) => !!s && s !== 'unspecified';
        let anyTaxStatusMissing = false;
        if (schedule === 'fixed' || schedule === '') {
            anyTaxStatusMissing = !isTaxStatusSpecified(partyA.tax?.claimSchedule?.all?.status) ||
                !isTaxStatusSpecified(partyB.tax?.claimSchedule?.all?.status);
            if (anyTaxStatusMissing) {
                taxes.errors.push('Tax filing status must be specified');
            }
            // Validate combined dependent children totals for fixed schedule
            validateDependentChildrenForYear('all');
        } else if (schedule === 'alternating') {
            anyTaxStatusMissing = !isTaxStatusSpecified(partyA.tax?.claimSchedule?.odd?.status) ||
                !isTaxStatusSpecified(partyB.tax?.claimSchedule?.odd?.status) ||
                !isTaxStatusSpecified(partyA.tax?.claimSchedule?.even?.status) ||
                !isTaxStatusSpecified(partyB.tax?.claimSchedule?.even?.status);
            if (anyTaxStatusMissing) {
                taxes.errors.push('Tax filing status must be specified');
            }
            // Validate combined dependent children totals for alternating schedule (both odd and even years)
            validateDependentChildrenForYear('odd');
            validateDependentChildrenForYear('even');
        }
    }

    // we get here, taxes has been validated.
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(taxes.errors ?? []).map(val => 'Taxes: ' + val));
    caseData.validation.childSupportDocs!.errors!.push(...(taxes.errors ?? []).map(e => e));
    caseData.validation.financialDocs!.errors!.push(...(taxes.errors ?? []).map(e => e));

    if (taxes.errors.length > 0) {
        caseData.validation.taxes = {
            ...taxes,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.taxes = {
            ...taxes,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateStepDowns(caseData: CaseData): boolean {

    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.stepDowns exists to preserve visited flag
    if (!caseData.validation.stepDowns) {
        caseData.validation.stepDowns = {};
    }
    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var stepDowns = caseData.validation.stepDowns;
    stepDowns.errors = [];

    const isVisited = stepDowns.visited === true;

    // if minor children are not involved, no need to validate the rest of the step downs page
    if (caseData.basic?.minorChildrenInvolved === false) {
        caseData.validation.stepDowns = {
            ...caseData.validation.stepDowns,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    // if children count is 0 or 1, no need to validate the rest of the step downs page
    const childrenCount = caseData.children?.count ?? 0;
    if (childrenCount <= 1) {
        caseData.validation.stepDowns = {
            ...caseData.validation.stepDowns,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        stepDowns.errors.push('Missing parties');
    } else {

        // Helper function to convert object to array if needed
        // Sometimes JSON deserialization converts arrays to objects with numeric keys
        const normalizeToArray = <T>(value: T | T[] | Record<string, T> | null | undefined): T[] => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'object') {
                // Convert object with numeric keys to array
                const keys = Object.keys(value).map(k => parseInt(k)).filter(k => !isNaN(k)).sort((a, b) => a - b);
                return keys.map(k => (value as Record<string, T>)[k.toString()]);
            }
            return [];
        };

        // Step downs array should have (childrenCount - 1) entries
        const stepDownsArrayA = normalizeToArray(partyA.tax?.stepDowns);
        const stepDownsArrayB = normalizeToArray(partyB.tax?.stepDowns);
        const maxLength = Math.max(stepDownsArrayA.length, stepDownsArrayB.length);

        // Validate each step down entry
        for (let i = 0; i < maxLength; i++) {
            const stepDownA = stepDownsArrayA[i];
            const stepDownB = stepDownsArrayB[i];

            // Skip if neither party has a step-down at this index
            if (!stepDownA && !stepDownB) {
                continue;
            }

            // Determine schedule (default to 'fixed' if not set)
            const schedule = stepDownA?.schedule || stepDownB?.schedule || 'fixed';

            // Get childrenCount from whichever stepDown exists (they should match)
            const stepDownChildrenCount = stepDownA?.childrenCount ?? stepDownB?.childrenCount ?? (childrenCount - 1 - i);

            if (schedule === 'fixed') {
                // For fixed schedule, both parties need all.status
                if (!stepDownA?.all?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyA.personal?.firstName || 'Party A'} ${partyA.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}), All Years`);
                }
                if (!stepDownB?.all?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyB.personal?.firstName || 'Party B'} ${partyB.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}), All Years`);
                }

                // Validate combined claimable children (age5Under + age6to16 + age17Over) for both parties
                const partyAAge5Under = stepDownA?.all?.age5Under || 0;
                const partyAAge6to16 = stepDownA?.all?.age6to16 || 0;
                const partyAAge17Over = stepDownA?.all?.age17Over || 0;
                const partyBAge5Under = stepDownB?.all?.age5Under || 0;
                const partyBAge6to16 = stepDownB?.all?.age6to16 || 0;
                const partyBAge17Over = stepDownB?.all?.age17Over || 0;
                const combinedTotal = partyAAge5Under + partyAAge6to16 + partyAAge17Over + partyBAge5Under + partyBAge6to16 + partyBAge17Over;

                if (combinedTotal > stepDownChildrenCount) {
                    stepDowns.errors.push(`Total children (${combinedTotal}) exceeds step-down count (${stepDownChildrenCount}) for Step Down ${stepDownChildrenCount}, All Years. Please reduce to ${stepDownChildrenCount} or less.`);
                }
            } else if (schedule === 'alternating') {
                // For alternating schedule, both parties need odd.status and even.status
                if (!stepDownA?.odd?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyA.personal?.firstName || 'Party A'} ${partyA.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}, Odd Years)`);
                }
                if (!stepDownB?.odd?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyB.personal?.firstName || 'Party B'} ${partyB.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}, Odd Years)`);
                }
                if (!stepDownA?.even?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyA.personal?.firstName || 'Party A'} ${partyA.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}, Even Years)`);
                }
                if (!stepDownB?.even?.status) {
                    stepDowns.errors.push(`Missing tax filing status for ${partyB.personal?.firstName || 'Party B'} ${partyB.personal?.lastName || ''} (Step Down ${stepDownChildrenCount}, Even Years)`);
                }

                // Validate combined claimable children for Odd Years
                const partyAOddAge5Under = stepDownA?.odd?.age5Under || 0;
                const partyAOddAge6to16 = stepDownA?.odd?.age6to16 || 0;
                const partyAOddAge17Over = stepDownA?.odd?.age17Over || 0;
                const partyBOddAge5Under = stepDownB?.odd?.age5Under || 0;
                const partyBOddAge6to16 = stepDownB?.odd?.age6to16 || 0;
                const partyBOddAge17Over = stepDownB?.odd?.age17Over || 0;
                const combinedOddTotal = partyAOddAge5Under + partyAOddAge6to16 + partyAOddAge17Over + partyBOddAge5Under + partyBOddAge6to16 + partyBOddAge17Over;

                if (combinedOddTotal > stepDownChildrenCount) {
                    stepDowns.errors.push(`Total children (${combinedOddTotal}) exceeds step-down count (${stepDownChildrenCount}) for Step Down ${stepDownChildrenCount}, Odd Years. Please reduce to ${stepDownChildrenCount} or less.`);
                }

                // Validate combined claimable children for Even Years
                const partyAEvenAge5Under = stepDownA?.even?.age5Under || 0;
                const partyAEvenAge6to16 = stepDownA?.even?.age6to16 || 0;
                const partyAEvenAge17Over = stepDownA?.even?.age17Over || 0;
                const partyBEvenAge5Under = stepDownB?.even?.age5Under || 0;
                const partyBEvenAge6to16 = stepDownB?.even?.age6to16 || 0;
                const partyBEvenAge17Over = stepDownB?.even?.age17Over || 0;
                const combinedEvenTotal = partyAEvenAge5Under + partyAEvenAge6to16 + partyAEvenAge17Over + partyBEvenAge5Under + partyBEvenAge6to16 + partyBEvenAge17Over;

                if (combinedEvenTotal > stepDownChildrenCount) {
                    stepDowns.errors.push(`Total children (${combinedEvenTotal}) exceeds step-down count (${stepDownChildrenCount}) for Step Down ${stepDownChildrenCount}, Even Years. Please reduce to ${stepDownChildrenCount} or less.`);
                }
            }
        }
    }

    // we get here, step downs has been validated.
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(stepDowns.errors ?? []).map(val => 'Step Downs: ' + val));
    caseData.validation.childSupportDocs!.errors!.push(...(stepDowns.errors ?? []).map(e => e));

    if (stepDowns.errors.length > 0) {
        caseData.validation.stepDowns = {
            ...stepDowns,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.stepDowns = {
            ...stepDowns,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateDeductions(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.deductions exists to preserve visited flag
    if (!caseData.validation.deductions) {
        caseData.validation.deductions = {};
    }

    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var deductions = caseData.validation.deductions;
    deductions.errors = [];

    const isVisited = caseData.validation.deductions?.visited === true;

    // if minor children are not involved, no need to validate the rest of the deductions page
    if (caseData.basic?.minorChildrenInvolved === false) {
        caseData.validation.deductions = {
            ...caseData.validation.deductions,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        deductions.errors.push('Missing parties');
    } else {
        // 2026 ignores child variance; only validate variance for tax year < 2026
        const taxYear = caseData.basic?.taxYear ?? 0;
        if (taxYear < 2026) {
            // If requesting variance, variance payment must be specified
            if (partyA.deductions?.isRequestingVariance === true) {
                if (!partyA.deductions?.variancePayment || partyA.deductions.variancePayment <= 0) {
                    deductions.errors.push(`Missing variance payment amount for ${partyA.personal?.firstName || 'Party A'}`);
                }
            }
            if (partyB.deductions?.isRequestingVariance === true) {
                if (!partyB.deductions?.variancePayment || partyB.deductions.variancePayment <= 0) {
                    deductions.errors.push(`Missing variance payment amount for ${partyB.personal?.firstName || 'Party B'}`);
                }
            }
        }
    }

    // Set validation level based on errors and visited status
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(deductions.errors ?? []).map(val => 'Deductions: ' + val));
    caseData.validation.childSupportDocs!.errors!.push(...(deductions.errors ?? []).map(e => e));

    if (deductions.errors.length > 0) {
        caseData.validation.deductions = {
            ...caseData.validation.deductions,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.deductions = {
            ...caseData.validation.deductions,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateMedical(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.medical exists to preserve visited flag
    if (!caseData.validation.medical) {
        caseData.validation.medical = {};
    }

    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var medical = caseData.validation.medical;
    medical.errors = [];

    const isVisited = caseData.validation.medical?.visited === true;

    // if minor children are not involved, no need to validate the rest of the medical page
    if (caseData.basic?.minorChildrenInvolved === false) {
        caseData.validation.medical = {
            ...caseData.validation.medical,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }

    const partyA = caseData.parties?.partyA;
    const partyB = caseData.parties?.partyB;

    if (!partyA || !partyB) {
        medical.errors.push('Missing parties');
    } else {
        // Validate that medical coverage type is specified (must be selected, 'none' is valid)
        if (!partyA.medical?.coverageTypeId && !partyB.medical?.coverageTypeId) {
            medical.errors.push(`Missing medical coverage type for one of ${partyA.personal?.firstName || 'Party A'} or ${partyB.personal?.firstName || 'Party B'}`);
        }

        // Validate that both parties don't have public coverage (Title XIX or HAWK-I)
        // This prevents the UI from getting stuck in a state where all fields show N/A
        const partyAHasPublicCoverage = partyA.medical?.coverageTypeId === 'titleXIX' || partyA.medical?.coverageTypeId === 'hawkI';
        const partyBHasPublicCoverage = partyB.medical?.coverageTypeId === 'titleXIX' || partyB.medical?.coverageTypeId === 'hawkI';

        if (partyAHasPublicCoverage && partyBHasPublicCoverage) {
            medical.errors.push(`Both parties cannot have public coverage (Title XIX or HAWK-I). Please update at least one party's coverage type.`);
        }

        // Validate that if coverage type is not 'none', monthly cost should be specified
        if (caseData.children.custodyArrangement !== 'shared') {
            if (partyA.medical?.coverageTypeId && (partyA.medical.coverageTypeId === 'privatePay' || partyA.medical.coverageTypeId === 'hawkI')) {
                if (partyA.medical?.monthlyMedicalCost == null || partyA.medical.monthlyMedicalCost < 0) {
                    medical.errors.push(`Missing monthly medical cost for ${partyA.personal?.firstName || 'Party A'}`);
                }
            }
            if (partyB.medical?.coverageTypeId && (partyB.medical.coverageTypeId === 'privatePay' || partyB.medical.coverageTypeId === 'hawkI')) {
                if (partyB.medical?.monthlyMedicalCost == null || partyB.medical.monthlyMedicalCost < 0) {
                    medical.errors.push(`Missing monthly medical cost for ${partyB.personal?.firstName || 'Party B'}`);
                }
            }
        }
    }

    // Set validation level based on errors and visited status
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(medical.errors ?? []).map(val => 'Medical: ' + val));
    caseData.validation.childSupportDocs!.errors!.push(...(medical.errors ?? []).map(e => e));

    if (medical.errors.length > 0) {
        caseData.validation.medical = {
            ...caseData.validation.medical,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.medical = {
            ...caseData.validation.medical,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateBudget(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.budget exists to preserve visited flag
    if (!caseData.validation.budget) {
        caseData.validation.budget = {};
    }
    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var budget = caseData.validation.budget;
    budget.errors = [];

    const isVisited = budget.visited === true;

    // Residential Arrangements Section - all questions required
    const residence = caseData.children?.residence;
    if (!residence?.parties || residence.parties === '') {
        budget.errors.push('Are both parties living in the same dwelling? is required');
    }
    if (!residence?.children || residence.children === '') {
        budget.errors.push('Do the children live in the family dwelling? is required');
    }
    if (!caseData.children?.physicalCare || caseData.children.physicalCare === '') {
        budget.errors.push('Which party has physical care of the children? is required');
    }

    // Affidavit Preferences Section - all questions required
    if (!caseData.basic?.affidavit?.type || caseData.basic.affidavit.type === '') {
        budget.errors.push('Which signature block would you like to use? is required');
    }

    // Set validation level based on errors and visited status
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(budget.errors ?? []).map(val => 'Medical: ' + val));
    caseData.validation.financialDocs!.errors!.push(...(budget.errors ?? []).map(e => e));

    if (budget.errors.length > 0) {
        caseData.validation.budget = {
            ...budget,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.budget = {
            ...budget,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateDivision(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.division exists to preserve visited flag
    if (!caseData.validation.division) {
        caseData.validation.division = {};
    }
    // Ensure metadata.errors exists
    if (!caseData.metadata) {
        caseData.metadata = {};
    }
    if (!caseData.metadata.errors) {
        caseData.metadata.errors = [];
    }

    var division = caseData.validation.division;
    division.errors = [];

    const isVisited = division.visited === true;

    // Helper function to calculate remaining amount for an asset
    const getRemainingAssetAmount = (asset: AssetModel): number => {
        const assetValue = asset.value || 0;
        const excludedAmount = 0; // Exclusions are now in allocation structure
        const availableAmount = assetValue - excludedAmount;

        const assetAllocation = asset.assetAllocation;
        const partyAAmount = assetAllocation?.partyA?.amount || 0;
        const partyBAmount = assetAllocation?.partyB?.amount || 0;

        // Calculate exclusions
        const partyAExclusions = (assetAllocation?.partyA?.exclusions || []).reduce((sum: number, excl: ExclusionItemModel) => sum + (excl.amount || 0), 0);
        const partyBExclusions = (assetAllocation?.partyB?.exclusions || []).reduce((sum: number, excl: ExclusionItemModel) => sum + (excl.amount || 0), 0);

        const totalAllocated = partyAAmount + partyBAmount + partyAExclusions + partyBExclusions;
        return availableAmount - totalAllocated;
    };

    // Helper function to calculate remaining debt amount for an asset
    const getRemainingAssetDebtAmount = (asset: AssetModel): number => {
        const debtValue = asset.encumbrance || 0;
        if (debtValue === 0) return 0;

        const debtAllocation = asset.debtAllocation;
        const partyAAmount = debtAllocation?.partyA?.amount || 0;
        const partyBAmount = debtAllocation?.partyB?.amount || 0;
        const totalAllocated = partyAAmount + partyBAmount;
        return debtValue - totalAllocated;
    };

    // Helper function to determine allocation status for asset
    const getAssetAllocationStatus = (asset: AssetModel): 'unallocated' | 'partially' | 'allocated' => {
        const remainingAmount = getRemainingAssetAmount(asset);
        const assetValue = asset.value || 0;
        const excludedAmount = 0;
        const availableAmount = assetValue - excludedAmount;

        // Determine asset allocation status
        const assetStatus: 'unallocated' | 'partially' | 'allocated' =
            Math.abs(remainingAmount) < 0.01 ? 'allocated' :
                Math.abs(remainingAmount - availableAmount) < 0.01 ? 'unallocated' :
                    'partially';

        // Check if asset has debt (encumbrance) that needs to be allocated
        const debtValue = asset.encumbrance || 0;
        if (debtValue !== 0) {
            const remainingDebtAmount = getRemainingAssetDebtAmount(asset);
            // Determine debt allocation status
            const debtStatus: 'unallocated' | 'partially' | 'allocated' =
                (remainingDebtAmount > -0.01 && remainingDebtAmount < 0.01) ? 'allocated' :
                    (remainingDebtAmount - debtValue > -0.01 && remainingDebtAmount - debtValue < 0.01) ? 'unallocated' :
                        'partially';

            // Combine statuses: both must be allocated for 'allocated'
            if (assetStatus === 'allocated' && debtStatus === 'allocated') {
                return 'allocated';
            } else if (assetStatus === 'unallocated' && debtStatus === 'unallocated') {
                return 'unallocated';
            } else {
                return 'partially';
            }
        }

        // No debt, return asset status only
        return assetStatus;
    };

    // Helper function to calculate remaining amount for a liability
    const getRemainingDebtAmount = (debt: LiabilityModel): number => {
        const debtValue = debt.encumbrance || 0;
        const debtAllocation = debt.debtAllocation;
        const partyAAmount = debtAllocation?.partyA?.amount || 0;
        const partyBAmount = debtAllocation?.partyB?.amount || 0;
        const totalAllocated = partyAAmount + partyBAmount;
        return debtValue - totalAllocated;
    };

    // Helper function to determine allocation status for debt
    const getDebtAllocationStatus = (debt: LiabilityModel): 'unallocated' | 'partially' | 'allocated' => {
        const remainingAmount = getRemainingDebtAmount(debt);
        const debtValue = debt.encumbrance || 0;

        if (remainingAmount > -0.01 && remainingAmount < 0.01) {
            return 'allocated';
        } else if (remainingAmount - debtValue > -0.01 && remainingAmount - debtValue < 0.01) {
            return 'unallocated';
        } else {
            return 'partially';
        }
    };

    // Get assets and liabilities with known values (exclude items with valueUnknownReason)
    const assets = caseData.financial?.assets || [];
    const liabilities = caseData.financial?.liabilities || [];

    const assetsWithKnownValues = assets.filter((asset: AssetModel) => {
        const reason = asset.valueUnknownReason as string | null | undefined;
        return !reason || reason === '';
    });

    const liabilitiesWithKnownValues = liabilities.filter((debt: LiabilityModel) => {
        const reason = debt.valueUnknownReason as string | null | undefined;
        return !reason || reason === '';
    });

    const totalAllocationCount = assetsWithKnownValues.length + liabilitiesWithKnownValues.length;

    // Count how many are fully allocated
    const allocatedAssetsCount = assetsWithKnownValues.filter((asset: AssetModel) =>
        getAssetAllocationStatus(asset) === 'allocated'
    ).length;

    const allocatedLiabilitiesCount = liabilitiesWithKnownValues.filter((debt: LiabilityModel) =>
        getDebtAllocationStatus(debt) === 'allocated'
    ).length;

    const allocatedCount = allocatedAssetsCount + allocatedLiabilitiesCount;

    // Validation: all count must match fully allocated count
    if (totalAllocationCount !== allocatedCount) {
        const unallocatedCount = totalAllocationCount - allocatedCount;
        division.errors.push(`${unallocatedCount} item${unallocatedCount !== 1 ? 's' : ''} ${unallocatedCount !== 1 ? 'are' : 'is'} not fully allocated`);
    }

    // Set validation level based on errors and visited status
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata.errors.push(...(division.errors ?? []).map(val => 'Step Downs: ' + val));
    //caseData.validation.childSupportDocs!.errors!.push(...(division.errors ?? []).map(e => e));
    // Division errors should not be included in financialDocs validation
    // caseData.validation.financialDocs!.errors!.push(...(division.errors ?? []).map(e => e));

    if (division.errors.length > 0) {
        caseData.validation.division = {
            ...division,
            level: isVisited ? 'warning' : 'notStarted'
        };
        return false;
    } else {
        caseData.validation.division = {
            ...division,
            level: isVisited ? 'success' : 'notStarted'
        };
        return true;
    }
}

export function validateNetworth(caseData: CaseData): boolean {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.networth exists to preserve visited flag
    if (!caseData.validation.networth) {
        caseData.validation.networth = {};
    }

    const networth = caseData.validation.networth;
    networth.errors = [];


    const isVisited = networth?.visited === true;

    // Networth page has no required fields - just mark as success if visited
    // duplicate the errors into the generic errors collection (so UI can go to one place to see all validation errors)
    caseData.metadata!.errors!.push(...(networth?.errors ?? []).map(val => 'Net Worth: ' + val));
    //caseData.validation.childSupportDocs!.errors!.push(...(caseData.validation.networth?.errors ?? []).map(e => e));
    caseData.validation.financialDocs!.errors!.push(...(networth?.errors ?? []).map(e => e));

    caseData.validation.networth = {
        ...networth,
        level: isVisited ? 'success' : 'notStarted'
    };

    return true;
}

/**
 * Validates the case data by checking for missing or invalid values.
 * @param caseData - The case data to validate
 * @returns A new CaseData object with validated values
 */
export function validateInputIA2026(caseData: CaseData): CaseData {
    // Ensure validation object exists
    if (!caseData.validation) {
        caseData.validation = {};
    }

    // Ensure validation.custody exists to preserve visited flag
    if (!caseData.validation.custody) {
        caseData.validation.custody = {};
    }

    // Validate case info tab
    // (we always validate this tab)
    validateCaseInfo(caseData);

    if (caseData.basic.minorChildrenInvolved === true) {
        // validate all tabs (both child support and financial related tabs)

        // Validate custody tab
        validateCustody(caseData);

        // Validate income tab
        validateIncome(caseData);

        // Validate taxes tab
        validateTaxes(caseData);

        // Validate deductions tab
        validateDeductions(caseData);

        // Validate medical tab
        validateMedical(caseData);

        // Validate step downs tab
        validateStepDowns(caseData);


    } else {
        // only validate financial related tabs

        // Validate income tab
        validateIncome(caseData);

        // Validate taxes tab
        validateTaxes(caseData);

    }

    // Validate networth tab (always validate, no required fields - just mark as success if visited)
    validateNetworth(caseData);

    // Validate budget tab (always validate)
    validateBudget(caseData);

    // Validate division tab (always validate)
    validateDivision(caseData);

    const childSupportDocs = caseData.validation.childSupportDocs!;
    childSupportDocs.level = (childSupportDocs.errors ?? []).length === 0 ? 'success' : (childSupportDocs.visited ? 'warning' : 'notStarted');

    const financialDocs = caseData.validation.financialDocs!;
    financialDocs.level = (financialDocs.errors ?? []).length === 0 ? 'success' : (financialDocs.visited ? 'warning' : 'notStarted');

    if ((caseData.metadata?.errors?.length ?? 0) > 0) {
        caseData.metadata!.errorLevel = 'validationError';
    }

    return caseData;
}

